import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { http } from '@/http/http';
import { Endpoints } from '@/http/endpoints';
import { getRumToken } from '@/auth/rum-token';
import { onTokenRotation } from '@/auth/auth-module';
import { createSubject, type SubjectLike, type Subscription } from './legacy-rxjs-shim';

/**
 * Portal SignalR — real-time push channel.
 *
 * Faithful port of the legacy `SignalRService`
 * (raptor-shell-ui `src/app/core/services/signal-r.service.ts`):
 *   1. negotiate a hub URL + access token from `/v1/SignalR/Negotiatesignalr`
 *   2. open a `HubConnection` with auto-reconnect
 *   3. add the connection to groups = clientId + the user's RUM role-guids
 *      (so the server can target messages per-client / per-role)
 *   4. subscribe to the four hub methods and re-emit each as
 *      `{ methodName, result }` on `portalMessageObservable`
 *
 * The shell exposes `portalMessageObservable` as the bare global
 * `EY_RAPToR_portalSignalRObservable`; legacy MFEs (and the incident banner)
 * subscribe to it for live updates. Mirrors legacy semantics including the
 * 60s-early token-expiry check and the per-client group move on client switch.
 */

// Hub method names — legacy `enums/signalr-remote-method.ts`.
export const HubMethod = {
  PortalUpdate: 'portalupdatemethod',
  RumUpdate: 'rumupdatemethod',
  DashboardUpdate: 'dashboardupdatemethod',
  PortalIncidentStatus: 'portalincidentstatusmethod',
  PortalNotification: 'portalnotificationmethod',
} as const;

const HUB_METHODS = [
  HubMethod.PortalUpdate,
  HubMethod.RumUpdate,
  HubMethod.DashboardUpdate,
  HubMethod.PortalIncidentStatus,
  HubMethod.PortalNotification,
] as const;

interface NegotiateResponse {
  accessToken: string;
  url: string;
}

export interface PortalMessage {
  methodName: string;
  result: unknown;
}

let connection: HubConnection | undefined;
let negotiateCache: NegotiateResponse | undefined;
let started = false;
let groupRefreshRegistered = false;

// Multicast subject backing `EY_RAPToR_portalSignalRObservable`.
const portalMessageSubject: SubjectLike<PortalMessage | undefined> = createSubject();

/** The observable legacy MFEs subscribe to via `EY_RAPToR_portalSignalRObservable`. */
export function getPortalMessageObservable() {
  return portalMessageSubject.observable;
}

/** Subscribe to portal SignalR messages from shell-side code (e.g. incidents). */
export function onPortalMessage(listener: (msg: PortalMessage) => void): Subscription {
  return portalMessageSubject.observable.subscribe((msg) => {
    if (msg) listener(msg);
  });
}

/** JWT exp check — expired 60s early (legacy `isSignalRTokenExpired`). */
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { exp?: number };
    if (typeof claims.exp !== 'number') return true;
    return claims.exp - 60 <= Date.now() / 1000;
  } catch {
    return true;
  }
}

/** Extract uppercase RUM role-guids from a RUM token (legacy `getRumRoleGuids`). */
function getRumRoleGuids(token: string): string[] {
  try {
    const payload = token.split('.')[1];
    if (!payload) return [];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as {
      roles?: Array<{ Roles?: Array<{ Identifier?: string }> }>;
    };
    const guids: string[] = [];
    for (const role of claims.roles ?? []) {
      for (const r of role.Roles ?? []) {
        if (r?.Identifier) guids.push(r.Identifier.toUpperCase());
      }
    }
    return guids;
  } catch {
    return [];
  }
}

function getCurrentClientId(): string {
  const w = window as unknown as { EY_RAPToR_getCurrentClientId?: () => string };
  try {
    return w.EY_RAPToR_getCurrentClientId?.() ?? '';
  } catch {
    return '';
  }
}

function getUserOid(): string {
  const w = window as unknown as { EY_RAPToR_getUserOID?: () => string };
  try {
    return w.EY_RAPToR_getUserOID?.() ?? '';
  } catch {
    return '';
  }
}

/** Negotiate a hub URL + token, caching until ~60s before the token expires. */
async function negotiate(): Promise<NegotiateResponse> {
  if (negotiateCache && !isTokenExpired(negotiateCache.accessToken)) {
    return negotiateCache;
  }
  const userOid = getUserOid();
  const res = await http<NegotiateResponse>(Endpoints.signalRNegotiate(), {
    method: 'PUT',
    json: userOid,
    headers: { Accept: 'application/json' },
  });
  negotiateCache = res;
  return res;
}

async function addConnectionToGroups(connectionId: string, groups: string[]): Promise<void> {
  await http(Endpoints.signalRAddToGroups(), {
    method: 'POST',
    json: { connectionId, groups },
    headers: { Accept: 'application/json' },
  });
}

async function removeConnectionFromAllGroups(connectionId: string): Promise<void> {
  await http(Endpoints.signalRRemoveFromAllGroups(), {
    method: 'DELETE',
    json: { connectionId },
    headers: { Accept: 'application/json' },
  });
}

/**
 * Add a connection to its client + role groups. clientId is always a group;
 * RUM role-guids are added when present (legacy
 * `addPortalConnectionToClientAndRoleGroup`).
 */
async function addToClientAndRoleGroups(connectionId: string): Promise<void> {
  const clientId = getCurrentClientId();
  const groups = [clientId].filter(Boolean);
  try {
    const rumToken = await getRumToken(clientId, 'Portal');
    if (rumToken) {
      for (const guid of getRumRoleGuids(rumToken)) groups.push(guid);
    }
  } catch (err) {
    console.warn('SignalR : getting RumToken failed : ' + String(err));
  }
  if (groups.length === 0) return;
  try {
    await addConnectionToGroups(connectionId, groups);
    for (const g of groups) {
      console.warn(`SignalR : ConnectionId : ${connectionId} added to the group : ${g}`);
    }
  } catch (err) {
    console.warn('SignalR : add to groups failed : ' + String(err));
  }
}

/**
 * Start the portal SignalR connection. Idempotent — no-op if a connection is
 * already connected/connecting/reconnecting. Mirrors legacy
 * `initiatePortalSignalR` + `startPortalSignalRConnection`.
 */
export async function initiatePortalSignalR(): Promise<void> {
  if (
    connection &&
    (connection.state === HubConnectionState.Connected ||
      connection.state === HubConnectionState.Connecting ||
      connection.state === HubConnectionState.Reconnecting)
  ) {
    return;
  }

  let negotiated: NegotiateResponse;
  try {
    negotiated = await negotiate();
  } catch (err) {
    console.warn('SignalR : negotiate failed : ' + String(err));
    return;
  }

  connection = new HubConnectionBuilder()
    .withUrl(negotiated.url, {
      accessTokenFactory: async () => (await negotiate()).accessToken,
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Error)
    .build();

  connection.onreconnected((connectionId) => {
    console.warn('SignalR : Connection reestablished with Id : ' + connectionId);
    if (connectionId) void addToClientAndRoleGroups(connectionId);
  });
  connection.onclose((error) => {
    console.warn('SignalR : Connection closed with error : ' + String(error));
  });

  // Subscribe to all hub methods → push `{ methodName, result }` to the subject.
  for (const method of HUB_METHODS) {
    connection.on(method, (result: unknown) => {
      portalMessageSubject.next({ methodName: method, result });
    });
  }

  try {
    await connection.start();
    started = true;
    console.warn('SignalR : connection started with Id : ' + connection.connectionId);
    if (connection.connectionId) await addToClientAndRoleGroups(connection.connectionId);
  } catch (err) {
    console.warn('SignalR : Connection failed : ' + String(err));
  }

  // Move groups whenever the active client changes. `markTokenRotationOnNextCall`
  // (fired by the client-store on every client switch) notifies token-rotation
  // listeners, so we reuse it as the client-change signal — legacy re-groups
  // the connection on `reloadMfeOnClientChange`. Registered once (idempotent
  // `initiatePortalSignalR` guards above prevent duplicate connections).
  if (!groupRefreshRegistered) {
    groupRefreshRegistered = true;
    onTokenRotation(() => {
      void refreshSignalRGroups();
    });
  }
}

/**
 * Move the connection to the new client's groups after a client switch:
 * drop all current groups, then re-add for the (now current) client + roles.
 * Legacy moves groups on `reloadMfeOnClientChange`.
 */
export async function refreshSignalRGroups(): Promise<void> {
  if (!connection || connection.state !== HubConnectionState.Connected) return;
  const connectionId = connection.connectionId;
  if (!connectionId) return;
  try {
    await removeConnectionFromAllGroups(connectionId);
  } catch (err) {
    console.warn('SignalR : remove from all groups failed : ' + String(err));
  }
  await addToClientAndRoleGroups(connectionId);
}

/** True once a connection has successfully started at least once. */
export function isSignalRStarted(): boolean {
  return started;
}

/**
 * Stop the portal SignalR connection. Called on logout so the hub is closed
 * explicitly (legacy `SignalRService.closeConnection`, invoked from
 * `AppContainerComponent.ngOnDestroy` when logout is triggered).
 */
export function closeConnection(): void {
  if (!connection) return;
  if (connection.state === HubConnectionState.Connected) {
    void connection
      .stop()
      .then(() => console.warn('SignalR : Connection stopped'))
      .catch((reason) =>
        console.warn('SignalR : Connection can not be closed : ' + String(reason)),
      );
  }
  connection = undefined;
  started = false;
}
